import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { usePresence } from '../contexts/PresenceContext';

const History: React.FC = () => {
  const { history, loadHistory, loading } = usePresence();
  const [fromDate, setFromDate] = useState<Date | null>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) // 30 giorni fa
  );
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [error, setError] = useState('');

  useEffect(() => {
    if (fromDate && toDate) {
      loadHistory(
        fromDate.toISOString().split('T')[0],
        toDate.toISOString().split('T')[0]
      );
    }
  }, [fromDate, toDate, loadHistory]);

  const handleSearch = () => {
    if (!fromDate || !toDate) {
      setError('Seleziona entrambe le date');
      return;
    }
    if (fromDate > toDate) {
      setError('La data di inizio deve essere precedente alla data di fine');
      return;
    }
    setError('');
    loadHistory(
      fromDate.toISOString().split('T')[0],
      toDate.toISOString().split('T')[0]
    );
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('it-IT');
  };

  const formatHours = (hours: number | null) => {
    if (!hours) return 'N/A';
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'warning';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'In Lavoro';
      case 'completed':
        return 'Completato';
      default:
        return status;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Storico Presenze
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtri di Ricerca
        </Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Data Inizio"
            type="date"
            value={fromDate ? fromDate.toISOString().split('T')[0] : ''}
            onChange={(e) => setFromDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Data Fine"
            type="date"
            value={toDate ? toDate.toISOString().split('T')[0] : ''}
            onChange={(e) => setToDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Cerca'}
          </Button>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data Check-in</TableCell>
                <TableCell>Data Check-out</TableCell>
                <TableCell>Ore Lavorate</TableCell>
                <TableCell>Straordinari</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !history || history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      Nessun record trovato per il periodo selezionato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      {formatDateTime(record.check_in)}
                    </TableCell>
                    <TableCell>
                      {record.check_out ? formatDateTime(record.check_out) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {formatHours(record.work_hours)}
                    </TableCell>
                    <TableCell>
                      {formatHours(record.overtime_hours)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(record.status)}
                        color={getStatusColor(record.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {record.notes || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default History;
